import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/FormField";
import { FORMS } from "@/config/forms";
import { computeScore } from "@/lib/scoring";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const isActive = (field, values) => {
  if (!field.showIf) return true;
  return values[field.showIf.field] === field.showIf.value;
};

export function AssessmentForm({ categoryKey, open, onClose, onSaved, assessment }) {
  const form = FORMS[categoryKey];
  const [values, setValues] = useState(assessment?.data || {});
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  if (!form) return null;

  const setValue = (name, val) => setValues((v) => ({ ...v, [name]: val }));

  const validate = () => {
    const errs = {};
    form.fields.forEach((f) => {
      const active = isActive(f, values);
      const val = values[f.name];
      const empty = val === undefined || val === null || val === "" || (Array.isArray(val) && val.length === 0) || (f.type === "geotag" && (!val || !val.lat || !val.lng)) || (f.type === "municipality" && (!val || !val.district || !val.name));
      if (f.required && active && empty) {
        errs[f.name] = "This field is required";
      } else if (!empty && f.pattern && f.type === "text") {
        if (!new RegExp(`^${f.pattern}$`).test(val)) errs[f.name] = "Invalid format";
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please fill all required fields correctly");
      return;
    }
    setSaving(true);
    try {
      const { score, max } = computeScore(form, values);
      const payload = { data: values, score: form.totalMarks ? score : null, max_score: form.totalMarks ? max : null };
      if (assessment) {
        await api.put(`/assessments/${assessment.id}`, payload);
        toast.success("Assessment resubmitted for approval");
      } else {
        await api.post("/assessments", { category: categoryKey, ...payload });
        toast.success("Assessment submitted successfully");
      }
      setValues({});
      setErrors({});
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to submit");
    } finally {
      setSaving(false);
    }
  };

  // Render all fields; conditional (showIf) fields stay visible but are only required when active.
  const visibleFields = form.fields;
  const sections = [];
  if (form.sections) {
    visibleFields.forEach((f) => {
      let sec = sections.find((s) => s.name === f.section);
      if (!sec) { sec = { name: f.section, fields: [] }; sections.push(sec); }
      sec.fields.push(f);
    });
  } else {
    sections.push({ name: null, fields: visibleFields });
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] p-0 gap-0 flex flex-col overflow-hidden" data-testid="assessment-form-dialog" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-200 shrink-0">
          <DialogTitle className="font-heading text-xl text-slate-900">{assessment ? `Edit & Resubmit — ${form.title}` : form.title}</DialogTitle>
          <DialogDescription className="text-sm text-slate-500">{assessment ? "Update the details and resubmit for approval." : (form.subtitle || "Complete all required fields and submit the assessment.")}</DialogDescription>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5 space-y-8 form-scroll">
          {sections.map((sec, i) => (
            <div key={i} className="space-y-5">
              {sec.name && (
                <div className="border-l-4 border-emerald-600 pl-3 py-1">
                  <h3 className="font-heading font-semibold text-emerald-700 uppercase tracking-wide text-sm">{sec.name}</h3>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {sec.fields.map((f) => {
                  const fullWidth = ["textarea", "checkbox", "geotag", "image", "municipality"].includes(f.type);
                  return (
                    <div key={f.name} className={fullWidth ? "md:col-span-2" : ""}>
                      <FormField field={f} value={values[f.name]} onChange={(v) => setValue(f.name, v)} error={errors[f.name]} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-slate-200 bg-white shrink-0 shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.1)]">
          <Button variant="outline" onClick={onClose} data-testid="assessment-cancel-btn">Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="assessment-submit-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {assessment ? "Resubmit" : "Submit Assessment"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
